import { useEffect } from 'react';
import { useAuthCheck } from '@/hooks/auth';
import { APP_ENV } from '@/types/env';

declare global {
  interface Window {
    chatwootSettings?: {
      hideMessageBubble?: boolean;
      position?: 'left' | 'right';
      locale?: string;
      type?: 'standard' | 'expanded_bubble';
      launcherTitle?: string;
    };
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setUser: (
        identifier: string,
        user: {
          name?: string;
          email?: string;
          company_name?: string;
          phone_number?: string;
          avatar_url?: string;
          identifier_hash?: string;
        },
      ) => void;
      setCustomAttributes: (attributes: Record<string, unknown>) => void;
      reset: () => void;
    };
  }
}

// Chatwootが追加したDOM要素の表示/非表示を切り替える
const setChatwootElementsVisibility = (visible: boolean) => {
  document.querySelectorAll<HTMLElement>('.woot-widget-holder, .woot--bubble-holder').forEach((el) => {
    el.style.display = visible ? '' : 'none';
  });
};

export const ChatwootWidget = () => {
  const { data } = useAuthCheck();

  // Chatwoot SDKをロード
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_CHATWOOT_BASE_URL;
    const websiteToken = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN;

    if (!baseUrl || !websiteToken) {
      return;
    }

    window.chatwootSettings = {
      hideMessageBubble: false,
      position: 'right',
      locale: 'ja',
      type: 'standard',
    };

    // SDKが既にロード済みなら既存のウィジェットを再表示するだけ
    if (window.chatwootSDK) {
      setChatwootElementsVisibility(true);
    } else {
      const script = document.createElement('script');
      script.src = `${baseUrl}/packs/js/sdk.js`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        window.chatwootSDK?.run({
          websiteToken,
          baseUrl,
        });
      };

      document.body.appendChild(script);
    }

    return () => {
      // DOM要素を非表示にする（削除するとSDKの内部参照が壊れるため非表示で対応し、会話履歴も保持する）
      setChatwootElementsVisibility(false);
    };
  }, []);

  // chatwoot:readyイベントでユーザー情報を設定
  useEffect(() => {
    const user = data.user;
    if (!data.isAuthenticated || !user) {
      return;
    }

    const handleChatwootReady = () => {
      try {
        if (window.$chatwoot) {
          const displayName = user.organization?.name
            ? `${user.name}（${user.organization.name}）`
            : user.name;

          window.$chatwoot.setUser(`${APP_ENV}-${user.id}`, {
            name: displayName,
            email: user.username,
          });
          window.$chatwoot.setCustomAttributes({
            userId: user.id,
            orgId: user.organization?.id,
            emailAddress: user.username,
            env: APP_ENV,
          });
        }
      } catch {
        // Chatwootが初期化されていない場合は無視
      }
    };

    window.addEventListener('chatwoot:ready', handleChatwootReady);

    if (window.$chatwoot) {
      handleChatwootReady();
    }

    return () => {
      window.removeEventListener('chatwoot:ready', handleChatwootReady);
    };
  }, [data.isAuthenticated, data.user]);

  return null;
};
